/**
 * The stat-card builder — list engine v2, "anyone can make one in four picks".
 *
 * Four sections beside a live preview, laid out as the mockup draws it: two
 * labelled pickers per row, the display as four small cards you choose between,
 * the audience as three pills. The count of picks is the point. A fifth is
 * where a builder stops being something a branch manager uses on a Tuesday and
 * becomes something they ask somebody else for.
 *
 * The preview is the real card, rendered by the real component, reading the
 * real number from the endpoint the saved card will use. A preview drawn by
 * different code is a preview of something else — which is how a dashboard
 * ends up with a figure nobody can reproduce.
 */
import { useEffect, useState, type ReactNode } from 'react'
import {
  CARD_ACCENTS, CARD_BUCKETS, CARD_PERIODS, CARD_PERIOD_LABELS, accentColor, cardProblem,
  describeFilter,
  type CardAudience, type CardBucket, type CardDefinition, type CardDisplay,
  type CardPeriod, type CardResult, type FilterGroup, type ListCard,
} from '@rowted/shared'
import { Modal } from './overlays.js'
import { Button, SectionLabel } from './primitives.js'
import { AdvancedFilter, filterLabels } from './list-filter.js'
import { FilterWell } from './filter-well.js'
import { MenuSelect } from './menu-select.js'
import { StatCard } from './stat-card.js'
import {
  IconBars, IconBullseye, IconCompany, IconLock, IconNumber, IconPlus, IconSparkles,
  IconTeam, IconTrend,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { useMe } from '../lib/use-me.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'
import type { CardDraft, UseCardsResult } from '../lib/use-cards.js'

export interface CardBuilderProps {
  onClose: () => void
  list: UseListResult
  /** What the list is called, for the dialog's "from the Jobs list". */
  listTitle: string
  cards: UseCardsResult
  /** The card being changed, or null to build a new one. */
  editing: ListCard | null
}

/**
 * A new card starts as the simplest true thing: how many of these there are.
 *
 * All of them — it does not inherit whatever the list happens to be filtered
 * by at the moment the builder opens. That was a card whose records depended
 * on where somebody was standing when they clicked New, and the two links
 * under "Of which records" make narrowing it a thing you do rather than a
 * thing that happened to you.
 */
function blankDefinition(): CardDefinition {
  return {
    label: '',
    measure: { fn: 'count' },
    time: { period: 'all_time' },
    compare: { kind: 'none' },
    display: 'number',
  }
}

/**
 * Mounted only while it is open, so its state starts from the card being
 * edited rather than from whatever was left in the boxes last time.
 */
export function CardBuilder({ onClose, list, listTitle, cards, editing }: CardBuilderProps) {
  const [draft, setDraft] = useState<CardDefinition>(
    () => editing?.definition ?? blankDefinition())
  const [audience, setAudience] = useState<CardAudience>(editing?.audience ?? 'me')
  const [orgUnitId, setOrgUnitId] = useState<string>(editing?.orgUnitId ?? '')
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [filtering, setFiltering] = useState(false)

  /*
   * The branches this person may share a card with, which is their own scope
   * and nothing wider — the server refuses an org unit outside it, and a
   * picker that offered one would be offering a save that fails.
   */
  const me = useMe()
  const scopeOptions = me.status === 'ready' ? me.me.scope.available : []

  useEffect(() => {
    if (!orgUnitId && scopeOptions.length > 0) setOrgUnitId(scopeOptions[0]!.id)
  }, [scopeOptions.length])

  const set = (patch: Partial<CardDefinition>) =>
    setDraft((current) => ({ ...current, ...patch }))

  const totalable = list.fields.filter((field) => field.aggregatable && field.numeric)
  const dateFields = list.fields.filter(
    (field) => field.kind === 'date' || field.kind === 'datetime')

  const measured = list.fields.find((field) => field.name === draft.measure.field)
  const unit: 'money' | 'number' = measured?.kind === 'money' ? 'money' : 'number'

  const labels = filterLabels(list)
  const readback = describeFilter(draft.filter, labels)

  const problem = cardProblem(draft)
  const ready = draft.label.trim().length > 0 && !problem

  const [result, setResult] = useState<CardResult | null>(null)
  const [previewing, setPreviewing] = useState(false)

  /*
   * What the preview asks about, and what the preview card draws — the same
   * object, so the two cannot disagree. The name is filled in because a
   * definition is stored with one and the endpoint parses the stored shape;
   * previewing before anybody has typed a name was a 400 every time the
   * builder opened.
   */
  const previewed: CardDefinition = { ...draft, label: draft.label.trim() || 'Untitled card' }
  // By value, not by identity: the effect re-fires when the definition actually
  // changes, not on every render of a freshly built object.
  const previewKey = JSON.stringify(previewed)

  useEffect(() => {
    if (problem) { setResult(null); setPreviewing(false); return }
    const controller = new AbortController()
    setPreviewing(true)
    const timer = setTimeout(() => {
      cards.preview(previewed, controller.signal)
        .then((value) => { if (!controller.signal.aborted) setResult(value) })
        .catch(() => { if (!controller.signal.aborted) setResult(null) })
        .finally(() => { if (!controller.signal.aborted) setPreviewing(false) })
    }, 300)
    return () => { controller.abort(); clearTimeout(timer) }
  }, [previewKey, problem])

  const save = async () => {
    setSaving(true)
    setFailure(null)
    const payload: CardDraft = {
      definition: { ...draft, label: draft.label.trim() },
      audience,
      orgUnitId: audience === 'scope' ? orgUnitId : null,
    }
    try {
      if (editing) await cards.update(editing.id, payload)
      else await cards.create(payload)
      onClose()
    } catch (cause: unknown) {
      setFailure(cause instanceof Error ? cause.message : 'The card could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={980}
      title={editing ? 'Edit card' : 'New card'}
      footer={
        <>
          <span className="mr-auto flex items-center gap-1.5 text-sub text-text-muted">
            <IconSparkles size={13} />
            {failure ?? `From the ${listTitle.toLowerCase()} list`}
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : editing ? 'Save Card' : 'Add Card'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <Field label="Name">
            <input
              value={draft.label}
              onChange={(event) => set({ label: event.target.value })}
              placeholder="Earned this quarter"
              maxLength={80}
              className={CONTROL}
            />
          </Field>

          {/* --- 1 ------------------------------------------------------- */}
          <Section label="1 · What to measure">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Count or total">
                <MenuSelect
                  size="field"
                  label="Count or total"
                  value={draft.measure.fn === 'count'
                    ? 'count'
                    : `${draft.measure.fn}:${draft.measure.field ?? ''}`}
                  onChange={(value) => {
                    const [fn, field] = (value ?? 'count').split(':')
                    set({
                      measure: fn === 'count'
                        ? { fn: 'count' }
                        : { fn: fn as 'sum' | 'avg', field },
                    })
                  }}
                  options={[
                    { value: 'count', label: 'Count of records' },
                    ...totalable.map((field) => ({
                      value: `sum:${field.name}`, label: `Sum of ${field.label}`,
                      icon: field.icon, group: 'Total of',
                    })),
                    ...totalable.map((field) => ({
                      value: `avg:${field.name}`, label: `Average ${field.label}`,
                      icon: field.icon, group: 'Average of',
                    })),
                  ]}
                  searchPlaceholder="Find a column"
                  className={CONTROL_MENU}
                  minWidth={260}
                />
              </Field>
            </div>

            <Grouped label="Show it as">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {DISPLAYS.map((option) => (
                  <DisplayCard
                    key={option.value}
                    {...option}
                    selected={draft.display === option.value}
                    onSelect={() => set({
                      display: option.value,
                      // A shape that needs buckets gets one, rather than sitting
                      // on an error until somebody works out which dropdown is
                      // missing.
                      ...(option.value === 'trend' || option.value === 'bars'
                        ? { time: { ...draft.time, by: draft.time.by ?? 'month' } }
                        : {}),
                      ...(option.value === 'target' && draft.compare.kind !== 'target'
                        ? { compare: { kind: 'target' as const, value: 0 } }
                        : {}),
                    })}
                  />
                ))}
              </div>
            </Grouped>
          </Section>

          {/* --- 2 ------------------------------------------------------- */}
          {/*
            * It starts at all of them, and narrowing it is chips: the same
            * quick popover the toolbar opens, writing into the card's filter
            * instead of the list's. The two links under it are the two
            * shortcuts — take what the list is already wearing, or go to the
            * builder for a group or a related-record condition.
            */}
          <Section label="2 · Which records">
            <FilterWell
              list={list}
              value={draft.filter}
              onChange={(next) => set({ filter: next })}
              onAdvanced={() => setFiltering(true)}
            />
          </Section>

          {/* --- 3 ------------------------------------------------------- */}
          <Section label="3 · Over what time">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Period">
                <MenuSelect
                  size="field"
                  label="Period"
                  value={draft.time.period}
                  onChange={(value) => set({
                    time: { ...draft.time, period: (value ?? 'all_time') as CardPeriod },
                  })}
                  options={CARD_PERIODS.map((period) => ({
                    value: period, label: CARD_PERIOD_LABELS[period],
                  }))}
                  className={CONTROL_MENU}
                  minWidth={220}
                />
              </Field>

              <Field label="By">
                <MenuSelect
                  size="field"
                  label="Group by"
                  disabled={draft.display !== 'trend' && draft.display !== 'bars'}
                  value={draft.time.by ?? null}
                  onChange={(value) => set({
                    time: { ...draft.time, by: (value ?? undefined) as CardBucket },
                  })}
                  options={CARD_BUCKETS.map((bucket) => ({ value: bucket, label: `By ${bucket}` }))}
                  clearable
                  clearLabel="Not bucketed"
                  placeholder="Not bucketed"
                  className={CONTROL_MENU}
                  minWidth={200}
                />
              </Field>

              <Field label="Date field">
                <MenuSelect
                  size="field"
                  label="Date to measure by"
                  value={draft.time.field ?? null}
                  onChange={(value) => set({
                    time: { ...draft.time, field: value ?? undefined },
                  })}
                  options={dateFields.map((field) => ({
                    value: field.name, label: field.label, icon: field.icon,
                  }))}
                  placeholder="Which date decides…"
                  searchPlaceholder="Find a date column"
                  className={CONTROL_MENU}
                  minWidth={220}
                />
              </Field>

              <Field label="Compare to">
                <MenuSelect
                  size="field"
                  label="Compare with"
                  value={draft.compare.kind}
                  onChange={(value) => {
                    const kind = (value ?? 'none') as
                      'none' | 'previous_period' | 'last_year' | 'target'
                    set({
                      compare: kind === 'target' ? { kind, value: 0 } : { kind },
                      // A Target card with nothing to aim at is the one
                      // combination the server refuses, so the two move
                      // together.
                      ...(kind !== 'target' && draft.display === 'target'
                        ? { display: 'number' as CardDisplay }
                        : {}),
                    })
                  }}
                  options={[
                    { value: 'none', label: 'Nothing' },
                    { value: 'previous_period', label: 'The period before' },
                    { value: 'last_year', label: 'Same period last year' },
                    { value: 'target', label: 'A target' },
                  ]}
                  className={CONTROL_MENU}
                  minWidth={220}
                />
              </Field>
            </div>

            {draft.compare.kind === 'target' && (
              <Field label="Target">
                <input
                  type="number"
                  aria-label="Target"
                  value={draft.compare.value}
                  onChange={(event) => set({
                    compare: { kind: 'target', value: Number(event.target.value) || 0 },
                  })}
                  className={cn(CONTROL, 'tnum')}
                />
              </Field>
            )}
          </Section>

          {/* --- 4 ------------------------------------------------------- */}
          <Section label="4 · Who sees it">
            <div className="flex flex-wrap items-center gap-1.5">
              <AudiencePill
                icon={<IconLock size={13} />}
                label="Just me"
                selected={audience === 'me'}
                onSelect={() => setAudience('me')}
              />
              <AudiencePill
                icon={<IconTeam size={13} />}
                label={scopeOptions.find((option) => option.id === orgUnitId)?.name ?? 'A branch'}
                selected={audience === 'scope'}
                onSelect={() => setAudience('scope')}
              />
              <AudiencePill
                icon={<IconCompany size={13} />}
                label="Everyone"
                selected={audience === 'all'}
                onSelect={() => setAudience('all')}
              />
              <span className="ml-auto text-sub text-text-muted">
                Clicking the card filters the list to its records
              </span>
            </div>

            {audience === 'scope' && scopeOptions.length > 1 && (
              <Field label="Which branch">
                <MenuSelect
                  size="field"
                  label="Branch"
                  value={orgUnitId}
                  onChange={(value) => { if (value) setOrgUnitId(value) }}
                  options={scopeOptions.map((option) => ({
                    value: option.id, label: option.name,
                  }))}
                  searchPlaceholder="Find a branch"
                  className={CONTROL_MENU}
                  minWidth={240}
                />
              </Field>
            )}
          </Section>
        </div>

        {/* The card itself, drawn by the component that will draw it. */}
        <div className="flex w-full shrink-0 flex-col gap-2 lg:w-[380px]">
          <SectionLabel>Live preview</SectionLabel>
          <StatCard
            definition={previewed}
            result={result}
            unit={unit}
            loading={previewing}
            dotColor={accentColor(draft.accent)}
            footnote={describeMeasure(draft, measured, listTitle)}
          />

          {/*
            * One colour, driving both the dot and the chart — they are the
            * same decision, and a card whose dot and sparkline disagreed read
            * as two cards overlapping. Named colours over a swatch grid,
            * because the value stored is a palette key and the palette has
            * seven of them.
            */}
          <Field label="Colour">
            <MenuSelect
              size="field"
              label="Colour"
              value={draft.accent ?? CARD_ACCENTS[0].value}
              onChange={(value) => { if (value) set({ accent: value }) }}
              options={CARD_ACCENTS.map((accent) => ({
                value: accent.value, label: accent.label, swatch: accentColor(accent.value),
              }))}
              className={CONTROL_MENU}
              minWidth={180}
            />
          </Field>

          {problem
            ? <p className="m-0 text-sub text-warn">{problem}</p>
            : (
              /*
               * The count and the reason for it, together: "412 records
               * counted — where Status is Completed" is checkable against
               * the chips above in a way a bare 412 is not.
               */
              <p className="m-0 text-sub leading-snug text-text-faint">
                {result
                  ? `${formatNumber(result.records)} records counted — ${
                    readback ? `where ${readback}` : `all ${listTitle.toLowerCase()}`}.`
                  : 'Reading the number…'}
              </p>
            )}
        </div>
      </div>

      {/*
        * The list's own builder, pointed at this card's filter. It renders its
        * own Modal on top of this one, which is why it sits outside the
        * layout above rather than inside a section of it.
        */}
      <AdvancedFilter
        list={list}
        open={filtering}
        title="Which records this card counts"
        value={draft.filter ?? null}
        onApply={(next: FilterGroup) => set({
          filter: next.conditions.length > 0 ? next : undefined,
        })}
        onClose={() => setFiltering(false)}
      />
    </Modal>
  )
}

const CONTROL = 'h-[34px] w-full rounded-field border border-line bg-surface px-2 text-cell text-text'

/*
 * The dropdowns carry their own border and padding — `cn` joins rather than
 * merges, so handing them `CONTROL` would leave `h-8 h-[34px]` on the element
 * and let the stylesheet pick. They take the width and nothing else.
 */
const CONTROL_MENU = 'w-full'

const DISPLAYS: {
  value: CardDisplay; icon: ReactNode; title: string; detail: string
}[] = [
  { value: 'number', icon: <IconNumber size={16} />, title: 'Number', detail: 'with change' },
  { value: 'trend', icon: <IconTrend size={16} />, title: 'Trend', detail: 'line over time' },
  { value: 'bars', icon: <IconBars size={16} />, title: 'Bars', detail: 'by period' },
  { value: 'target', icon: <IconBullseye size={16} />, title: 'Target', detail: 'progress to goal' },
]

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  )
}

/** A labelled control — the 11.5px muted caption the mockup puts above each. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex w-full min-w-0 flex-col gap-1">
      <span className="text-sub text-text-muted">{label}</span>
      {children}
    </label>
  )
}

/**
 * `Field`, but a `div`. A `<label>` names one control; wrapping the four
 * display buttons in one made every button's accessible name start with
 * "Show it as", which is a name nobody would read out.
 */
function Grouped({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      <span className="text-sub text-text-muted">{label}</span>
      {children}
    </div>
  )
}

function DisplayCard({
  icon, title, detail, selected, onSelect,
}: {
  icon: ReactNode
  title: string
  detail: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-2.5 rounded-card border px-3 py-2 text-left',
        selected
          ? 'border-accent-line bg-accent-soft ring-2 ring-accent-ring'
          : 'border-line bg-surface hover:bg-hover',
      )}
    >
      <span className={cn('shrink-0', selected ? 'text-accent' : 'text-text-muted')}>{icon}</span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-cell font-semibold text-text">{title}</span>
        <span className="truncate text-sub text-text-muted">{detail}</span>
      </span>
    </button>
  )
}

function AudiencePill({
  icon, label, selected, onSelect,
}: { icon: ReactNode; label: string; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-button border px-2.5 text-cell font-semibold',
        selected
          ? 'border-accent-line bg-accent-soft text-accent-dark'
          : 'border-line bg-surface text-text-body hover:bg-hover',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

/** The footnote under a card: what the number actually is. */
export function describeMeasure(
  definition: CardDefinition, field: ListFieldDef | undefined, listTitle = 'records',
): string {
  const shape = definition.display === 'number' ? 'Number'
    : definition.display === 'trend' ? 'Trend'
      : definition.display === 'bars' ? 'Bars' : 'Target'

  const measure = definition.measure.fn === 'count'
    ? `count of ${listTitle.toLowerCase()}`
    : `${definition.measure.fn === 'avg' ? 'average' : 'sum'} of `
      + `${field?.label ?? definition.measure.field ?? 'a column'}`

  const when = definition.time.field
    ? `${field ? '' : ''}${CARD_PERIOD_LABELS[definition.time.period].toLowerCase()}`
    : null

  return [shape, measure, when, definition.time.by ? `by ${definition.time.by}` : null]
    .filter(Boolean).join(' · ')
}

export { IconPlus as CardBuilderPlusIcon }
